// src/components/calendar/edit/PublicHoliday.js
import { useContext } from "react";
import { enqueueSnackbar } from "notistack";
import axiosInstance from "../../../config/axiosInstance";
import { logApiError } from "../../../config/apiErrorHandling";
import { CalendarContext } from "../../context/CalendarContext";

const buildPublicHolidayReplaceScopes = ({ ci, day, periodStart }) => {
  const scopes = [
    { kind: "employee_day", ci: String(ci), day },
    { kind: "day", day },
    { kind: "employee_period", ci: String(ci), category: "rest_between_days" },
  ];
  if (periodStart && day === periodStart) {
    scopes.push({ kind: "employee_period", ci: String(ci), category: "rotation_opening" });
  }
  return scopes;
};

/* === helpers: overview multi-rol (mismo formato que en Workshift/FreeDay) === */
const formatOverview = (items = []) => {
  if (!items.length) return "Multirol: sin asignaciones ese día.";
  const roles = items.map((it) => {
    const role = it.role || (it.work ? "principal" : "auxiliar");
    const wa = it.workarea_name ?? "Área";
    const ws = it.workshift?.name ?? "S/A";
    return `${role} en ${wa} (${ws})`;
  });
  return `Multirol: ${roles.join(", ")}. Total áreas: ${items.length}`;
};

const showOverview = async ({ ci, day, station_id }) => {
  try {
    const { data } = await axiosInstance.get("/calendar/multi_role_overview", {
      params: { ci, day, station_id: String(station_id) },
    });
    enqueueSnackbar(formatOverview(data?.items || []), {
      variant: "info",
      autoHideDuration: 6000,
    });
  } catch {
    /* no bloquea */
  }
};
/* === fin helpers === */

export const useHandleTogglePublicHoliday = () => {
  const {
    calendarData,
    setCalendarData,
    mergeCalendarValidationPatch,
  } = useContext(CalendarContext);

  return async (
    ci,
    day,
    is_public_holiday,
    work_public_holiday,
    station_id,
    position_id,
    setLoadingHoliday = () => {},
    onClose = () => {},
    confirm = false
  ) => {
    setLoadingHoliday(true);

    if (!ci || !day || !station_id || !position_id) {
      enqueueSnackbar("Error: Datos insuficientes para modificar el feriado", { variant: "error" });
      setLoadingHoliday(false);
      return { ok: false };
    }

    if (!is_public_holiday) {
      enqueueSnackbar("La fecha no está configurada como feriado oficial.", { variant: "warning" });
      // igual mostramos el overview para contexto
      showOverview({ ci, day, station_id });
      setLoadingHoliday(false);
      return { ok: false };
    }

    const newWorkPublicHolidayState = !work_public_holiday;

    try {
      const { data } = await axiosInstance.post(
        "/calendar/update_public_holiday",
        {},
        {
          params: {
            ci,
            day,
            add_public_holiday: String(newWorkPublicHolidayState), // "true"/"false"
            station_id: String(station_id),
            position_id: String(position_id),
            calendar_id: calendarData?._id ?? "",
            ...(confirm ? { confirm: true } : {}),
          },
        }
      );

      if (data?.updated_calendar) {
        enqueueSnackbar(
          newWorkPublicHolidayState
            ? "Empleado marcado como no trabaja feriado."
            : "Empleado marcado como trabaja feriado.",
          { variant: "success" }
        );
        setCalendarData(data.updated_calendar);
        mergeCalendarValidationPatch({
          errors: data.errors,
          warnings: data.warnings,
          replaceScopes: buildPublicHolidayReplaceScopes({
            ci,
            day,
            periodStart: calendarData?.start_date,
          }),
        });
        return { ok: true, data };
      } else {
        enqueueSnackbar(data?.message || "Error al modificar el feriado", { variant: "error" });
        return { ok: false, data };
      }
    } catch (error) {
      const info = logApiError(
        "calendar.public_holiday.toggle",
        error,
        "No se pudo modificar el feriado. Intenta nuevamente."
      );
      if (!(info.status === 409 && info.needsConfirmation)) {
        enqueueSnackbar(info.message, { variant: info.status === 409 ? "warning" : "error" });
        // aun en error (no de confirmación), mostramos el overview
        showOverview({ ci, day, station_id });
      }
      return { ok: false, error, info };
    } finally {
      setLoadingHoliday(false);
      onClose();
    }
  };
};
